import type {
  QuartileEvent,
  WebediaPlayer
} from '@webedia-services/webedia-player';
import loadScript from 'load-script';

import type {
  ConfStreamingAnalytics,
  ContentStreamTag,
  EStatPlayerState,
  EStatTag
} from 'types/mediametrie';

import { getVendorsConsent } from 'common/consent/Didomi';
import { isProd } from 'common/tools/env';
import { urlParts } from 'common/tools/network/url';
import { getKeys } from 'common/types';

import {
  MEDIAMETRIE_LIB_URL,
  MEDIAMETRIE_STREAMING_SERIAL
} from 'website/configuration/constants';
import type { PlayerCustomData } from 'website/module/player';
import {
  getVideoFormat,
  getVideoTheme,
  getVideoDisplayContext
} from 'website/tracking/player/googleAnalytics.internals';
import { getCurrentCustomData } from 'website/tracking/player/utils';

declare global {
  interface Window {
    eStatTag?: EStatTag;
  }
}

type SendArgs = {
  action: EStatPlayerState;
  actionPosition?: number;
  streamVal: ConfStreamingAnalytics;
};

/**
 * This flag is used to determine whether mediametrie's lib script was loaded on the page
 * true: we start to send hit to mediametrie
 * false: we save push hit into a temporary stack and will send it later in next hit request
 * @type {Boolean}
 */
let libLoaded = false;
/**
 * mediametrie lib api
 */
let contentStreamTag: ContentStreamTag | undefined;
/**
 * This is a temporary stack to store the hits
 * @type {Array}
 */
let trackerStacks: SendArgs[] = [];
/**
 * callbackPosition
 * see also https://portail-marqueurs.mediametrie.fr/marker-portal/documentations#_Toc403654482
 * @return {[type]}        [description]
 * @param webediaPlayer
 */
const getPosition = (webediaPlayer: WebediaPlayer) =>
  Math.round(webediaPlayer.getPosition());

/**
 * This is an initial config object for mediametrie
 * see also https://portail-marqueurs.mediametrie.fr/marker-portal/documentations#_Toc403654482
 * @type {Object}
 */
const confMediametrieAnalytics: ConfStreamingAnalytics = {
  consentType: 'optin',
  domain: '',
  measure: 'streaming',
  serial: MEDIAMETRIE_STREAMING_SERIAL,
  streaming: {
    diffusion: 'replay',
    playerName: 'jwplayer',
    streamName: ''
  }
};

/**
 * This object is used to generate the value of streaming config in each circumstance
 * @type {Object}
 */
const configStreaming: {
  [key in keyof ConfStreamingAnalytics['streaming']]?: {
    getValue: (
      webediaPlayer: WebediaPlayer,
      customData: PlayerCustomData
    ) => ConfStreamingAnalytics['streaming'][key];
  };
} = {
  playerVersion: {
    getValue: webediaPlayer => {
      const version = webediaPlayer.getVersion();
      return typeof version === 'string'
        ? version.split('+')[0] // jwplayer v7 player version structure x.x.x+pluginname
        : '';
    }
  },
  callbackPosition: {
    getValue: webediaPlayer => getPosition.bind(null, webediaPlayer)
  },
  streamDuration: {
    getValue: webediaPlayer => `${Math.round(webediaPlayer.getDuration() ?? 0)}`
  },
  streamName: {
    getValue: (webediaPlayer, { trackingIdentifier }) => {
      if (!webediaPlayer.postroll?.isPlayingPostrollVideo()) {
        return trackingIdentifier;
      }

      return (
        webediaPlayer.postroll?.getCurrentPostroll()?.xt_title?.toLowerCase() ??
        ''
      );
    }
  }
};

/**
 * This function is used to set a streaming object and send hit by using mediametrie api
 * @param  {String} action    The hit action's name (play, pause, stop)
 * @param  {Number} actionPosition
 * @param  {Object} streamVal The streaming config object
 * @return {void}
 */
const send = ({ action, actionPosition, streamVal }: SendArgs) => {
  contentStreamTag?.set(streamVal);
  // ready to send
  if (isProd()) {
    contentStreamTag?.notifyPlayer(action, actionPosition);
  }
};

/**
 * This function is used to generate a streaming object and determine whether we save or send this hit
 * @param {WebediaPlayer} webediaPlayer
 * @param {String} eventAction The hit action's name (play, pause, stop)
 * @param {PlayerCustomData} customData
 * @param playerName
 * @param  {Object, undefined} eventParam extra parameters sent during the hit (usually the position of the hit for seek event)
 * @return {void}
 */
export const trackerHandler = (
  webediaPlayer: WebediaPlayer,
  eventAction: EStatPlayerState,
  customData: PlayerCustomData,
  playerName: string,
  eventParam?: { actionPosition?: number }
) => {
  const streamingObj: SendArgs = {
    action: eventAction,
    streamVal: {
      consentType: 'optin',
      domain: '',
      measure: 'streaming',
      serial: '',
      streaming: {
        diffusion: 'replay',
        playerName,
        streamName: ''
      }
    },
    ...(eventParam ?? {})
  };

  getKeys(configStreaming).forEach(value => {
    streamingObj.streamVal.streaming = {
      ...streamingObj.streamVal.streaming,
      [value]: configStreaming[value]?.getValue(webediaPlayer, customData)
    };
  });

  if (libLoaded && typeof contentStreamTag !== 'undefined') {
    for (const tracker of trackerStacks) {
      send(tracker);
    }

    trackerStacks = [];

    send(streamingObj);
  } else {
    trackerStacks.push(streamingObj);
  }
};

export const quartileEventTracking = (
  webediaPlayer: WebediaPlayer,
  { detail: { quartile } }: QuartileEvent,
  customData: PlayerCustomData,
  playerName: string
) => {
  if (quartile === 0) {
    trackerHandler(webediaPlayer, 'play', customData, playerName);
  } else if (quartile === 4) {
    trackerHandler(webediaPlayer, 'stop', customData, playerName);
  }
};

/**
 * This function is used to asynchronously load mediametrie lib, set flag libLoaded to true and init mediametrie api
 * @return {void}
 */
export const loadMediametrieLib = async (
  webediaPlayer: WebediaPlayer,
  customData: PlayerCustomData
) => {
  const hasMediametrieConsent = await getVendorsConsent(['769']);
  if (typeof contentStreamTag === 'undefined') {
    loadScript(MEDIAMETRIE_LIB_URL, () => {
      const currentCustomData = getCurrentCustomData(customData, webediaPlayer);
      const {
        context,
        id,
        relatedEntityId,
        relatedEntityTitle,
        relatedEntityType,
        title,
        trackingIdentifier,
        duration
      } = currentCustomData;

      const locationRef =
        context === 'iblogvision' && document.referrer
          ? urlParts(document.referrer)
          : null;
      const domainName = locationRef
        ? locationRef.host
        : window.location.hostname;

      const href = locationRef
        ? `${locationRef.protocol}//${locationRef.host}/${locationRef.host}${locationRef.path}`
        : window.location.href;
      libLoaded = true;
      confMediametrieAnalytics.domain = domainName;
      confMediametrieAnalytics.streaming.callbackPosition =
        webediaPlayer.getPosition;
      confMediametrieAnalytics.streaming.streamName = trackingIdentifier;
      confMediametrieAnalytics.streaming.streamDuration = `${duration}`;
      confMediametrieAnalytics.levels = {
        level_1: getVideoTheme(currentCustomData),
        level_2: getVideoFormat(currentCustomData),
        level_3: title ?? undefined,
        level_4: domainName
      };
      confMediametrieAnalytics.newLevels = {
        newLevel_1: getVideoDisplayContext(currentCustomData),
        newLevel_2: `${id}`,
        newLevel_3: `${(
          relatedEntityType ?? ''
        ).toLowerCase()}_${relatedEntityId}`,
        newLevel_4: relatedEntityTitle,
        newLevel_7: href
      };
      confMediametrieAnalytics.consentType = hasMediametrieConsent
        ? 'optin'
        : 'optout';

      if (window.eStatTag) {
        contentStreamTag = new window.eStatTag(confMediametrieAnalytics);
      }
    });
  }
};
